/**
 * 레이아웃 데이터 관리 훅
 * 룸, 좌석, 구역, 기기, 건축요소 통합 관리
 */

import { useState, useCallback, useEffect } from 'react';
import { layoutRepository } from '../repositories';
import { layoutService } from '../services';
import type {
  Room,
  Seat,
  Zone,
  Device,
  ArchitectureElement,
  ZoneCreateRequest,
  DraggableItem,
  GridPosition,
  SelectedItem,
} from '../models';

export const useLayoutData = () => {
  // 데이터 상태
  const [selectedRoom, setSelectedRoom] = useState<string | null>(null);
  const [rooms, setRooms] = useState<Room[]>([]);
  const [seats, setSeats] = useState<Seat[]>([]);
  const [zones, setZones] = useState<Zone[]>([]);
  const [devices, setDevices] = useState<Device[]>([]);
  const [architectureElements, setArchitectureElements] = useState<ArchitectureElement[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // UI 상태
  const [selectedItem, setSelectedItem] = useState<SelectedItem | null>(null);
  const [viewMode, setViewMode] = useState<'all' | 'seats' | 'devices'>('all');
  const [scale, setScale] = useState(1);
  const [saving, setSaving] = useState(false);

  // ==================== 데이터 로드 ====================

  /**
   * 모든 데이터 로드 (룸, 좌석, 구역, 기기, 건축요소)
   */
  const loadAllData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // 룸 목록 가져오기
      const roomsData = await layoutRepository.getRooms();
      setRooms(roomsData || []);

      // 현재 선택된 룸 유지 또는 첫 번째 룸 선택
      // ⚠️ 선택된 룸이 이번에 불러온 방 목록에 없으면(=다른 매장 편집 후 넘어온 stale 값)
      //    반드시 첫 번째 룸으로 재설정한다. 안 그러면 저장 시 타 매장 room_id가 실려
      //    좌석이 남의 매장 방으로 오염됨(2026-07-22 우장산 사고).
      let currentRoomId = selectedRoom;
      const isStale = !!currentRoomId && !(roomsData || []).some((r) => r.id === currentRoomId);
      if ((!currentRoomId || isStale) && roomsData && roomsData.length > 0) {
        // 첫 번째 룸 선택 (room_default 포함)
        currentRoomId = roomsData[0]?.id;
      }
      if (currentRoomId) {
        setSelectedRoom(currentRoomId);
        await loadRoomData(currentRoomId);
      }
    } catch (err) {
      console.error('데이터 로드 실패:', err);
      setError(err instanceof Error ? err.message : '데이터를 불러오는데 실패했습니다');
    } finally {
      setLoading(false);
    }
  }, []); // dependency 제거 - 항상 최신 rooms를 로드

  /**
   * 특정 룸의 데이터 로드 (좌석, 구역, 기기, 건축요소)
   */
  const loadRoomData = useCallback(async (roomId: string) => {
    try {
      // 구역 정보
      const zonesData = await layoutRepository.getZones(roomId);
      setZones(zonesData || []);

      // 좌석 정보
      const seatsData = await layoutRepository.getSeats(roomId);
      setSeats(seatsData || []);

      // 기기 정보
      const devicesData = await layoutRepository.getDevices();
      setDevices(devicesData || []);

      // 건축 요소 (localStorage)
      const architectureData = layoutRepository.loadArchitectureElements(roomId);
      setArchitectureElements(architectureData || []);
    } catch (err) {
      console.error('룸 데이터 로드 실패:', err);
      throw err;
    }
  }, []);

  /**
   * 룸 변경 시 데이터 재로드
   */
  useEffect(() => {
    if (selectedRoom) {
      loadRoomData(selectedRoom);
    }
  }, [selectedRoom, loadRoomData]);

  /**
   * 초기 데이터 로드
   */
  useEffect(() => {
    loadAllData();
  }, []);

  // ==================== Zone 관리 ====================

  const createZone = useCallback(async (data: ZoneCreateRequest): Promise<boolean> => {
    // 유효성 검증
    const validation = layoutService.validateZoneData(data);
    if (!validation.valid) {
      alert(validation.message);
      return false;
    }

    try {
      await layoutRepository.createZone(data);
      if (selectedRoom) {
        await loadRoomData(selectedRoom);
      }
      return true;
    } catch (error) {
      console.error('구역 생성 실패:', error);
      alert('구역 생성에 실패했습니다.');
      return false;
    }
  }, [selectedRoom, loadRoomData]);

  const updateZone = useCallback(
    async (zoneId: string, data: ZoneCreateRequest): Promise<boolean> => {
      // 유효성 검증
      const validation = layoutService.validateZoneData(data);
      if (!validation.valid) {
        alert(validation.message);
        return false;
      }

      try {
        await layoutRepository.updateZone(zoneId, data);
        if (selectedRoom) {
          await loadRoomData(selectedRoom);
        }
        return true;
      } catch (error) {
        console.error('구역 수정 실패:', error);
        alert(error instanceof Error && error.message ? error.message : '구역 수정에 실패했습니다.');
        return false;
      }
    },
    [selectedRoom, loadRoomData]
  );

  const deleteZone = useCallback(
    async (zoneId: string): Promise<boolean> => {
      // 확인은 화면에서 두 번 누르기로 받는다 (window.confirm 금지 — 환경에 따라 막혀 '반응 없음'으로 보임)
      try {
        await layoutRepository.deleteZone(zoneId);
        if (selectedRoom) {
          await loadRoomData(selectedRoom);
        }
        return true;
      } catch (error) {
        console.error('구역 삭제 실패:', error);
        // 서버가 알려준 이유를 그대로 보여준다 (예: 순찰 QR 이 붙은 구역)
        alert(error instanceof Error && error.message ? error.message : '구역 삭제에 실패했습니다.');
        return false;
      }
    },
    [selectedRoom, loadRoomData]
  );

  // ==================== Seat 관리 ====================

  const deleteSeat = useCallback(async (seatNumber: number): Promise<boolean> => {
    if (!window.confirm(`좌석 ${seatNumber}번을 삭제하시겠습니까?`)) return false;

    try {
      await layoutRepository.deleteSeat(seatNumber);
      setSeats((prev) => prev.filter((s) => s.seat_number !== seatNumber));
      setSelectedItem(null);
      return true;
    } catch (err) {
      console.error('좌석 삭제 오류:', err);
      alert('좌석 삭제 중 오류가 발생했습니다.');
      return false;
    }
  }, []);

  // ==================== Device 관리 ====================

  const createDevice = useCallback(
    async (deviceData: Partial<Device>): Promise<boolean> => {
      // 유효성 검증
      const validation = layoutService.validateDeviceData({
        device_name: deviceData.device_name,
        room_id: selectedRoom || undefined,
      });
      if (!validation.valid) {
        alert(validation.message);
        return false;
      }

      try {
        const fullData = {
          ...deviceData,
          room_id: selectedRoom!,
          id: `device_${Date.now()}`,
          last_seen: new Date().toISOString(),
        };

        const savedDevice = await layoutRepository.createDevice(fullData);
        setDevices((prev) => [...prev, savedDevice]);
        return true;
      } catch (err) {
        console.error('기기 추가 오류:', err);
        const statusMatch = err instanceof Error && err.message.match(/HTTP (\d+)/);
        const status = statusMatch ? parseInt(statusMatch[1]) : 0;
        alert(`기기 추가 실패: ${status === 404 ? 'API를 찾을 수 없습니다' : err}`);
        return false;
      }
    },
    [selectedRoom]
  );

  const updateDevice = useCallback(async (deviceId: string, data: Partial<Device>): Promise<boolean> => {
    try {
      const updated = await layoutRepository.updateDevice(deviceId, data);
      setDevices((prev) => prev.map((d) => (d.id === deviceId ? updated : d)));
      return true;
    } catch (err) {
      console.error('기기 수정 오류:', err);
      alert('기기 수정에 실패했습니다.');
      return false;
    }
  }, []);

  const deleteDevice = useCallback(async (deviceId: string): Promise<boolean> => {
    if (!window.confirm('이 기기를 삭제하시겠습니까?')) return false;

    try {
      await layoutRepository.deleteDevice(deviceId);
      setDevices((prev) => prev.filter((d) => d.id !== deviceId));
      setSelectedItem(null);
      return true;
    } catch (err) {
      console.error('기기 삭제 오류:', err);
      alert('기기 삭제 중 오류가 발생했습니다.');
      return false;
    }
  }, []);

  // ==================== Architecture 관리 ====================

  const createArchitecture = useCallback(
    (elementData: Partial<ArchitectureElement>): boolean => {
      if (!selectedRoom) {
        alert('룸을 선택해주세요.');
        return false;
      }

      try {
        const fullData: ArchitectureElement = {
          id: `arch_${Date.now()}`,
          room_id: selectedRoom,
          element_type: elementData.element_type as any,
          position_x: elementData.position_x || 0,
          position_y: elementData.position_y || 0,
          width: 1, // 그리드 1칸
          height: 1, // 그리드 1칸
          rotation: elementData.rotation || 0,
          wall_position: elementData.wall_position,
          attributes: {
            material: 'concrete',
            color: '#6B7280',
            opacity: 1,
          },
        };

        setArchitectureElements((prev) => [...prev, fullData]);
        return true;
      } catch (err) {
        console.error('건축 요소 추가 오류:', err);
        alert('건축 요소 추가 중 오류가 발생했습니다.');
        return false;
      }
    },
    [selectedRoom]
  );

  const updateArchitecture = useCallback((elementId: string, data: Partial<ArchitectureElement>): boolean => {
    try {
      setArchitectureElements((prev) =>
        prev.map((el) => (el.id === elementId ? { ...el, ...data } : el))
      );
      return true;
    } catch (err) {
      console.error('건축 요소 수정 오류:', err);
      return false;
    }
  }, []);

  const deleteArchitecture = useCallback((elementId: string): boolean => {
    if (!window.confirm('이 건축 요소를 삭제하시겠습니까?')) return false;

    setArchitectureElements((prev) => prev.filter((el) => el.id !== elementId));
    setSelectedItem(null);
    return true;
  }, []);

  // ==================== Drag & Drop ====================

  const handleDrop = useCallback((item: DraggableItem, position: GridPosition) => {
    if (item.type === 'seat') {
      setSeats((prev) =>
        prev.map((seat) =>
          seat.seat_number === item.data.seat_number
            ? { ...seat, position_x: position.x, position_y: position.y }
            : seat
        )
      );
    } else if (item.type === 'device') {
      setDevices((prev) =>
        prev.map((device) =>
          device.id === item.data.id ? { ...device, position_x: position.x, position_y: position.y } : device
        )
      );
    } else if (item.type === 'architecture') {
      // 건축요소도 그리드 좌표 사용 (좌석/기기와 동일)
      setArchitectureElements((prev) =>
        prev.map((element) =>
          element.id === item.data.id
            ? { ...element, position_x: position.x, position_y: position.y }
            : element
        )
      );
    }
  }, []);

  // ==================== Layout 저장 ====================

  const saveLayout = useCallback(async (): Promise<boolean> => {
    try {
      if (!selectedRoom) {
        console.error('❌ saveLayout: selectedRoom이 없음');
        return false;
      }

      console.log('💾 saveLayout 시작:', { selectedRoom, totalSeats: seats.length });
      setSaving(true);

      // 좌석 위치 개별 업데이트 (room_id가 없거나 selectedRoom과 일치하는 좌석)
      const roomSeats = seats.filter((seat) => !seat.room_id || seat.room_id === selectedRoom);
      console.log(`📊 저장할 좌석 수: ${roomSeats.length}개`);

      let successCount = 0;
      let failCount = 0;

      for (const seat of roomSeats) {
        try {
          console.log(`📤 좌석 ${seat.seat_number} 저장 중... (x: ${seat.position_x}, y: ${seat.position_y})`);

          // zone_id가 빈 문자열이면 null로 변환 (DB에서 integer 타입이므로)
          const zoneId = seat.zone_id && seat.zone_id !== '' ? seat.zone_id : null;

          await layoutRepository.updateSeatPosition(seat.seat_number, {
            position_x: seat.position_x,
            position_y: seat.position_y,
            direction: seat.direction || 'north',
            room_id: selectedRoom, // 현재 선택된 룸으로 설정
            zone_id: zoneId,
          });
          console.log(`✅ 좌석 ${seat.seat_number} 저장 성공`);
          successCount++;
        } catch (seatErr) {
          console.error(`💥 좌석 ${seat.seat_number} 업데이트 오류:`, seatErr);
          failCount++;
        }
      }

      console.log(`💾 저장 완료: 성공 ${successCount}개, 실패 ${failCount}개`);


      // 기기 위치 개별 업데이트
      const roomDevices = devices.filter((d) => d.room_id === selectedRoom);
      for (const device of roomDevices) {
        try {
          await layoutRepository.updateDevicePosition(device.id, {
            position_x: device.position_x,
            position_y: device.position_y,
            rotation: device.rotation || 0,
          });
        } catch (deviceErr) {
          console.error(`💥 기기 ${device.device_name} 업데이트 오류:`, deviceErr);
        }
      }

      // 건축 요소 저장 (localStorage)
      layoutRepository.saveArchitectureElements(selectedRoom, architectureElements);

      alert('레이아웃이 저장되었습니다');
      setSaving(false);
      return true;
    } catch (err) {
      console.error('💥 레이아웃 저장 실패:', err);
      alert('레이아웃 저장에 실패했습니다: ' + (err instanceof Error ? err.message : '알 수 없는 오류'));
      setSaving(false);
      return false;
    }
  }, [selectedRoom, seats, devices, architectureElements]);

  return {
    // 데이터 상태
    selectedRoom,
    rooms,
    seats,
    zones,
    devices,
    architectureElements,
    loading,
    error,

    // UI 상태
    selectedItem,
    viewMode,
    scale,
    saving,

    // 상태 변경 함수
    setSelectedRoom,
    setSelectedItem,
    setViewMode,
    setScale,

    // 데이터 로드
    loadAllData,
    loadRoomData,

    // Zone 관리
    createZone,
    updateZone,
    deleteZone,

    // Seat 관리
    deleteSeat,

    // Device 관리
    createDevice,
    updateDevice,
    deleteDevice,

    // Architecture 관리
    createArchitecture,
    updateArchitecture,
    deleteArchitecture,

    // Drag & Drop
    handleDrop,

    // Layout 저장
    saveLayout,
  };
};
